import { ErrorAlert } from "#components/ErrorAlert"
import { Alert, AlertDescription, AlertTitle } from "#components/ui/alert"
import { Button } from "#components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { Checkbox } from "#components/ui/checkbox"
import { Label } from "#components/ui/label"
import { useAuth } from "#contexts/AuthContext"
import { useI18n } from "#hooks/use-i18n"
import { cn } from "#lib/utils"
import { createCreateUserValidator } from "@home-finance-manager/shared/validators/users.validator.factory"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircle2, Mail } from "lucide-react"
import { useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"
import { FormDivider } from "./forms/FormDivider"
import { FormField } from "./forms/FormField"
import { SocialLoginButtons } from "./forms/SocialLoginButtons"
import { TermsFooter } from "./forms/TermsFooter"

const createRegisterSchema = (t: (key: string) => string) =>
  createCreateUserValidator(t)
    .extend({
      confirmPassword: z.string(),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: t("components.register.validation.acceptTerms"),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("components.register.validation.passwordsDontMatch"),
      path: ["confirmPassword"],
    })

interface RegisterFormProps {
  onSwitchToLogin: () => void
  className?: string
  invitationToken?: string
}

export function RegisterForm({ onSwitchToLogin, className, invitationToken }: RegisterFormProps) {
  const { t } = useI18n()
  const { register: registerUser, isLoading, error } = useAuth()
  const [showVerificationMessage, setShowVerificationMessage] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState<string>("")

  const registerSchema = useMemo(() => createRegisterSchema(t), [t])
  type RegisterFormData = z.infer<typeof registerSchema>

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
  })

  const onSubmit = async (data: RegisterFormData) => {
    const result = await registerUser(data.email, data.password, invitationToken)
    if (result.success) {
      setRegisteredEmail(data.email)
      setShowVerificationMessage(true)
      reset()
    }
  }

  if (showVerificationMessage) {
    return (
      <div className={cn("max-w-md w-full", className)}>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-xl">
              {t("components.register.verificationEmailSent")}
            </CardTitle>
            <CardDescription>{t("components.register.checkYourEmail")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertTitle>{t("components.register.verificationRequired")}</AlertTitle>
              <AlertDescription className="mt-2">
                {t("components.register.verificationEmailSentTo", { email: registeredEmail })}
                <br />
                <br />
                {t("components.register.verificationEmailInstructions")}
              </AlertDescription>
            </Alert>
            <div className="mt-4 text-center text-sm text-muted-foreground">
              {t("components.register.redirectingToDashboard")}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={cn("max-w-md w-full", className)}>
      <Card className="">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t("components.register.title")}</CardTitle>
          <CardDescription>{t("components.register.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-6">
              <SocialLoginButtons
                onAppleLogin={() => {
                  console.log("Sign up with Apple")
                }}
                onGoogleLogin={() => {
                  console.log("Sign up with Google")
                }}
                appleText={t("components.register.signUpWithApple")}
                googleText={t("components.register.signUpWithGoogle")}
              />
              <FormDivider />
              <div className="grid gap-6">
                <FormField
                  name="email"
                  control={control}
                  label={t("components.register.email")}
                  type="email"
                  placeholder="m@example.com"
                  error={errors.email}
                />
                <FormField
                  name="password"
                  control={control}
                  label={t("components.register.password")}
                  type="password"
                  error={errors.password}
                />
                <FormField
                  name="confirmPassword"
                  control={control}
                  label={t("components.register.confirmPassword")}
                  type="password"
                  error={errors.confirmPassword}
                />
                <div className="flex items-center space-x-2">
                  <Controller
                    name="acceptTerms"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="acceptTerms"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label htmlFor="acceptTerms" className="text-sm font-normal">
                    {t("components.register.acceptTerms")}{" "}
                    <a href="#" className="underline underline-offset-4 hover:text-primary">
                      {t("components.register.termsOfService")}
                    </a>{" "}
                    {t("components.register.and")}{" "}
                    <a href="#" className="underline underline-offset-4 hover:text-primary">
                      {t("components.register.privacyPolicy")}
                    </a>
                  </Label>
                </div>
                {errors.acceptTerms && (
                  <p className="text-sm text-red-500">{errors.acceptTerms.message}</p>
                )}
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t("components.register.loading") : t("components.register.signUp")}
                </Button>
                <ErrorAlert error={error} className="mt-4" />
              </div>
              <div className="text-center text-sm">
                {t("components.register.alreadyHaveAccount")}{" "}
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  className="underline underline-offset-4 hover:text-primary"
                >
                  {t("components.register.login")}
                </button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <TermsFooter />
    </div>
  )
}
